import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="customer-select"
export default class extends Controller {
  static targets = ["select", "selectedCustomers", "counter", "clearLink", "quickAllResidents", "quickapplicantsWithoutLeases", "quickExchangeStudents", "quickInternationalStudents", "allResidentsCount", "applicantsWithoutLeasesCount", "exchangeStudentsCount", "internationalStudentsCount", "quickSelectionField"]
  static values = { 
    maxItems: Number,
    leasesUrl: String
  }

  connect() {
    console.log('🎯 Customer select controller connected')
    // Wait for DOM to be fully ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.initializeSelectize())
    } else {
      setTimeout(() => this.initializeSelectize(), 200)
    }
  }

  disconnect() {
    if (this.selectize) {
      this.selectize.destroy()
      this.selectize = null
    }
  }

  initializeSelectize() {
    const maxItems = this.hasMaxItemsValue ? this.maxItemsValue : null

    // Ensure jQuery and selectize are available
    if (!window.$ || !window.$.fn.selectize) {
      return
    }

    if (!this.selectTarget) {
      return
    }

    try {
      const $element = $(this.selectTarget)
      
      this.selectize = $element.selectize({
        persist: false,
        create: false,
        maxItems: maxItems,
        diacritics: true,
        valueField: 'id',
        labelField: 'name',
        loadThrottle: 500,
        placeholder: 'Hae...',
        searchField: ['first_name', 'last_name', 'identity_number', 'email'],
        options: [],
        render: {
          item: (item, escape) => {
            // Return empty/hidden item since we display them in custom section
            return '<div style="display: none;"></div>'
          },
          option: (item, escape) => {
            return `
              <div>
                ${item.name ? `<span class='name'>${escape(item.name)}</span>&nbsp;` : ""}
                ${item.email ? `<span class='email'>&lt;${escape(item.email)}&gt;</span>` : ""}
                ${item.identity_number ? 
                  `&nbsp;<span class='identity_number'>(${escape(item.identity_number)})</span>` : 
                  `&nbsp;<span class='date_of_birth'>(${escape(item.date_of_birth)})</span>`
                }
              </div>
            `
          }
        },
        load: (query, callback) => {
          if (!query || query.length < 1) {
            return callback()
          }

          $.ajax({
            url: "/admin/customers",
            type: 'GET',
            dataType: 'json',
            data: {
              select: true,
              q: query
            },
            success: (data) => {
              callback(data.customers || [])
            },
            error: (xhr, status, error) => {
              callback()
            }
          })
        },
        onChange: (value) => {
          this.updateSelectedCustomersDisplay()
        },
        onItemAdd: (value, item) => {
          this.updateSelectedCustomersDisplay()
        },
        onItemRemove: (value) => {
          this.updateSelectedCustomersDisplay()
        }
      })[0].selectize

      // Initialize the display
      this.updateSelectedCustomersDisplay()

      // Handle pre-selected customer from URL parameter
      this.handlePreSelectedCustomer()

    } catch (error) {
      // Error handling without logging
    }
  }

  updateSelectedCustomersDisplay() {
    if (!this.hasSelectedCustomersTarget || !this.hasCounterTarget) {
      return
    }
    
    // Clear and rebuild entire display
    this.selectedCustomersTarget.innerHTML = ''
    
    let totalCount = 0
    let displayItems = []
    
    // Add quick selection chips first
    const quickSelections = this.getCurrentQuickSelections()
    
    if (quickSelections.length > 0) {
      // Fetch real total count for all quick selections via AJAX
      this.fetchQuickSelectionCounts(quickSelections).then(response => {
        const totalCount = response.total_count || 0
        
        quickSelections.forEach(selectionType => {
          const chipData = this.getQuickSelectionChipData(selectionType)
          if (chipData) {
            displayItems.push({
              type: 'quick-selection',
              id: selectionType,
              name: chipData.groupName,
              count: chipData.count, // Still show individual static count on chip
              data: { selectionType }
            })
          }
        })
        
        // Render all display items
        this.renderDisplayItems(displayItems)
        
        // Update counter and controls with real total count
        this.updateCounterAndControls(totalCount, false) // No longer approximate
      }).catch(() => {
        // Fallback to static counts if AJAX fails
        quickSelections.forEach(selectionType => {
          const chipData = this.getQuickSelectionChipData(selectionType)
          if (chipData) {
            displayItems.push({
              type: 'quick-selection',
              id: selectionType,
              name: chipData.groupName,
              count: chipData.count,
              data: { selectionType }
            })
            totalCount += chipData.count
          }
        })
        
        // Render all display items
        this.renderDisplayItems(displayItems)
        
        // Update counter and controls (still approximate)
        this.updateCounterAndControls(totalCount, true)
      })
    } else {
      // Add individual customers if no quick selections are active
      if (this.selectize) {
        const selectedItems = this.selectize.items || []
        const selectedOptions = selectedItems.map(id => this.selectize.options[id]).filter(Boolean)
        
        selectedOptions.forEach(customer => {
          displayItems.push({
            type: 'customer',
            id: customer.id,
            name: customer.name,
            identity_number: customer.identity_number,
            date_of_birth: customer.date_of_birth,
            data: { customer }
          })
        })
        totalCount = selectedItems.length
      }
      
      // Render all display items
      this.renderDisplayItems(displayItems)
      
      // Update counter and controls
      this.updateCounterAndControls(totalCount, false)
    }
  }

  // Centralized configuration for all quick selection chips
  // To add a new chip:
  // 1. Add configuration here with selectionType, targetName, buttonTarget, and groupName
  // 2. Add the corresponding target to static targets array
  // 3. Add the button element in the template with:
  //    - data-customer-select-target="[buttonTarget]"  
  //    - data-action="click->customer-select#selectQuickSelectionChip"
  //    - data-selection-type="[selectionType]"
  //    - data-chip-name="[groupName]"
  // 4. Add count element with data-customer-select-target="[targetName]"
  // 5. Add server-side scope in Customer model and helper method
  // 6. Add case in support_tickets_controller.rb handle_quick_selection method
  getQuickSelectionConfigs() {
    return {
      'all_residents': {
        targetName: 'allResidentsCount',
        buttonTarget: 'quickAllResidents',
        groupName: 'Kaikki asukkaat'
      },
      'applicants_without_leases': {
        targetName: 'applicantsWithoutLeasesCount',
        buttonTarget: 'quickapplicantsWithoutLeases',
        groupName: 'Kaikki hakijat'
      },
      'exchange_students': {
        targetName: 'exchangeStudentsCount',
        buttonTarget: 'quickExchangeStudents',
        groupName: 'Vaihto-opiskelijat'
      },
      'international_students': {
        targetName: 'internationalStudentsCount',
        buttonTarget: 'quickInternationalStudents',
        groupName: 'Kansainväliset opiskelijat'
      }
    }
  }

  getQuickSelectionChipData(selectionType) {
    const configs = this.getQuickSelectionConfigs()
    const config = configs[selectionType]

    if (!config) {
      return null
    }

    // Dynamically get the count target
    const countTargetName = `has${config.targetName.charAt(0).toUpperCase() + config.targetName.slice(1)}Target`
    const countTarget = this[`${config.targetName}Target`]
    
    if (this[countTargetName] && countTarget) {
      return {
        count: parseInt(countTarget.textContent) || 0,
        groupName: this.getChipName(selectionType) // Use stored chip name or fallback to config
      }
    }

    return null
  }

  renderDisplayItems(displayItems) {
    const maxVisible = 20

    displayItems.forEach((item, index) => {
      const itemDiv = this.createDisplayItem(item, index >= maxVisible)
      this.selectedCustomersTarget.appendChild(itemDiv)
    })

    // Add show all/show less toggle if there are more than maxVisible items
    if (displayItems.length > maxVisible) {
      this.addToggleLink(displayItems.length - maxVisible)
    }
  }

  createDisplayItem(item, isHidden = false) {
    const itemDiv = document.createElement('div')
    itemDiv.className = 'selected-customer-item'
    
    if (item.type === 'quick-selection') {
      itemDiv.classList.add('quick-selection-group')
      itemDiv.setAttribute('data-quick-selection-group', item.id)
    }

    if (isHidden) {
      itemDiv.style.display = 'none'
      itemDiv.classList.add('hidden-customer')
    }

    // Apply consistent styling
    itemDiv.style.cssText += `
      display: ${isHidden ? 'none' : 'inline-block'};
      margin: 2px 4px 2px 0;
      padding: 4px 8px;
      background: #2B383F;
      border: 1px solid #ddd;
      border-radius: 4px;
      font-size: 12px;
      line-height: 1.3;
      color: #fff;
    `

    // Set content based on item type
    if (item.type === 'quick-selection') {
      itemDiv.innerHTML = this.getQuickSelectionHTML(item)
      this.attachQuickSelectionHandlers(itemDiv, item)
    } else {
      itemDiv.innerHTML = this.getCustomerHTML(item)
      this.attachCustomerHandlers(itemDiv, item)
    }
    
    return itemDiv
  }

  getQuickSelectionHTML(item) {
    return `
      <span class='quick-group-name'>${this.escapeHtml(item.name)}</span>
      <i class="fa fa-times remove-quick-group-btn" data-selection-type="${item.id}" 
        style="margin-left: 6px; cursor: pointer; font-size: 11px;" title="Poista"></i>
    `
  }

  getCustomerHTML(item) {
    return `
      ${item.name ? `<span class='name'>${this.escapeHtml(item.name)}</span>&nbsp;` : ""}
      ${item.identity_number ? 
        `<span class='identity_number'>${this.escapeHtml(item.identity_number)}</span>` : 
        `<span class='date_of_birth'>${this.escapeHtml(item.date_of_birth || '')}</span>`
      }
      <i class="fa fa-times remove-customer-btn" data-customer-id="${item.id}" 
        style="margin-left: 6px; cursor: pointer; font-size: 11px;" title="Poista"></i>
    `
  }

  attachQuickSelectionHandlers(itemDiv, item) {
    const removeButton = itemDiv.querySelector('.remove-quick-group-btn')
    removeButton.addEventListener('click', (e) => {
      e.preventDefault()
      this.removeQuickSelectionGroup(item.id)
    })
  }

  attachCustomerHandlers(itemDiv, item) {
    const removeButton = itemDiv.querySelector('.remove-customer-btn')
    removeButton.addEventListener('click', (e) => {
      e.preventDefault()
      this.removeCustomer(item.id)
    })
  }

  async fetchQuickSelectionCounts(selectionTypes) {
    const countsUrl = "/admin/customers/quick_selection_counts"

    try {
      const response = await fetch(countsUrl + `?selection_types=${selectionTypes.join(',')}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
        }
      })

      if (response.ok) {
        const data = await response.json()
        return data // Return the entire response object which contains total_count
      } else {
        throw new Error(`HTTP ${response.status}`)
      }
    } catch (error) {
      // Return empty object to trigger fallback behavior
      return { total_count: 0 }
    }
  }

  updateCounterAndControls(totalCount, isApproximate = false) {
    // Update counter
    if (this.hasCounterTarget) {
      if (isApproximate) {
        this.counterTarget.textContent = `~${totalCount}` // Show approximate count
      } else {
        this.counterTarget.textContent = totalCount
      }
    }

    // Show/hide clear link
    if (this.hasClearLinkTarget) {
      this.clearLinkTarget.style.display = totalCount > 0 ? 'inline' : 'none'
    }
  }

  addToggleLink(hiddenCount) {
    const toggleDiv = document.createElement('div')
    toggleDiv.className = 'cms_toggle-customers-div'

    const toggleLink = document.createElement('a')
    toggleLink.href = '#'
    toggleLink.className = 'cms_toggle-customers-link'
    toggleLink.textContent = `Näytä kaikki (+${hiddenCount})`

    let showingAll = false

    toggleLink.addEventListener('click', (e) => {
      e.preventDefault()
      const hiddenCustomers = this.selectedCustomersTarget.querySelectorAll('.hidden-customer')

      if (showingAll) {
        // Hide extra customers
        hiddenCustomers.forEach(customer => {
          customer.style.display = 'none'
        })
        toggleLink.textContent = `Näytä kaikki (+${hiddenCount})`
        showingAll = false
      } else {
        // Show all customers
        hiddenCustomers.forEach(customer => {
          customer.style.display = 'inline-block'
        })
        toggleLink.textContent = 'Näytä vähemmän'
        showingAll = true
      }
    })

    toggleDiv.appendChild(toggleLink)
    this.selectedCustomersTarget.appendChild(toggleDiv)
  }

  removeCustomer(customerId) {
    if (this.selectize) {
      this.selectize.removeItem(customerId)
      // updateSelectedCustomersDisplay will be called by onItemRemove callback
    }
  }

  clearAllCustomers(event) {
    event.preventDefault()
    this.clearAllSelections()
    // updateSelectedCustomersDisplay will be called by onChange callback for regular selections
    this.updateSelectedCustomersDisplay()
  }

  escapeHtml(text) {
    const div = document.createElement('div')
    div.textContent = text
    return div.innerHTML
  }

  // Generic quick selection handler
  selectQuickSelectionChip(event) {
    event.preventDefault()
    const selectionType = event.currentTarget.dataset.selectionType
    const chipName = event.currentTarget.dataset.chipName

    console.log(`🚀 Quick selection called: ${selectionType}`)

    // Check if already selected
    if (this.isQuickSelectionActive(selectionType)) {
      return // Already selected, do nothing
    }

    // Store the chip name from the template for this selection
    this.addQuickSelection(selectionType, chipName)
  }

  isQuickSelectionActive(selectionType) {
    return this.selectedCustomersTarget.querySelector(`[data-quick-selection-group="${selectionType}"]`) !== null
  }

  addQuickSelection(selectionType, chipName = null) {
    // Clear any regular customer selections first
    if (this.selectize) {
      this.selectize.clear()
    }

    // Get current selections from hidden field
    const currentSelections = this.getCurrentQuickSelections()

    // Add new selection if not already present
    if (!currentSelections.includes(selectionType)) {
      currentSelections.push(selectionType)
    }

    // Store the chip name if provided (from template data-chip-name)
    if (chipName) {
      this.storeChipName(selectionType, chipName)
    }

    // Update hidden field with all selections
    if (this.hasQuickSelectionFieldTarget) {
      this.quickSelectionFieldTarget.value = currentSelections.join(',')
    }

    // Update button states
    this.updateQuickSelectionButtonStates()

    // Disable selectize when quick selections are active
    this.updateSelectizeState()

    // Update the unified display
    this.updateSelectedCustomersDisplay()
  }

  getCurrentQuickSelections() {
    if (!this.hasQuickSelectionFieldTarget || !this.quickSelectionFieldTarget.value) {
      return []
    }
    return this.quickSelectionFieldTarget.value.split(',').filter(s => s.length > 0)
  }

  // Store chip names from template data-chip-name attributes
  storeChipName(selectionType, chipName) {
    if (!this.chipNames) {
      this.chipNames = {}
    }
    this.chipNames[selectionType] = chipName
  }

  // Get stored chip name or fallback to config
  getChipName(selectionType) {
    // First try to get from stored template data
    if (this.chipNames && this.chipNames[selectionType]) {
      return this.chipNames[selectionType]
    }

    // Fallback to configuration
    const configs = this.getQuickSelectionConfigs()
    const config = configs[selectionType]
    return config ? config.groupName : selectionType
  }

  updateQuickSelectionButtonStates() {
    const configs = this.getQuickSelectionConfigs()
    const activeSelections = this.getCurrentQuickSelections()

    // Update all configured buttons
    Object.entries(configs).forEach(([selectionType, config]) => {
      const hasTargetMethod = `has${config.buttonTarget.charAt(0).toUpperCase() + config.buttonTarget.slice(1)}Target`
      const targetProperty = `${config.buttonTarget}Target`

      if (this[hasTargetMethod] && this[targetProperty]) {
        if (activeSelections.includes(selectionType)) {
          this[targetProperty].classList.add('disabled')
        } else {
          this[targetProperty].classList.remove('disabled')
        }
      }
    })
  }

  updateSelectizeState() {
    if (!this.selectize) {
      return
    }

    const quickSelections = this.getCurrentQuickSelections()
    const hasQuickSelections = quickSelections.length > 0

    if (hasQuickSelections) {
      // Disable selectize when quick selections are active
      this.selectize.disable()
    } else {
      // Enable selectize when no quick selections are active
      this.selectize.enable()
    }
  }

  removeQuickSelectionGroup(selectionType) {
    // Get current selections and remove the specified one
    const currentSelections = this.getCurrentQuickSelections()
    const updatedSelections = currentSelections.filter(s => s !== selectionType)

    // Update hidden field
    if (this.hasQuickSelectionFieldTarget) {
      this.quickSelectionFieldTarget.value = updatedSelections.join(',')
    }

    // Update button states
    this.updateQuickSelectionButtonStates()

    // Update selectize state (enable if no quick selections left)
    this.updateSelectizeState()

    // Update the unified display
    this.updateSelectedCustomersDisplay()
  }

  clearAllSelections() {
    // Clear regular selectize selections
    if (this.selectize) {
      this.selectize.clear()
    }

    // Clear quick selection
    if (this.hasQuickSelectionFieldTarget) {
      this.quickSelectionFieldTarget.value = ''
    }

    // Reset button states
    this.updateQuickSelectionButtonStates()

    // Re-enable selectize when all selections are cleared
    this.updateSelectizeState()

    // Update the unified display
    this.updateSelectedCustomersDisplay()
  }

  handlePreSelectedCustomer() {
    if (!this.selectize || !this.selectTarget) return

    // Find pre-selected options and add them to selectize
    Array.from(this.selectTarget.options)
      .filter(option => option.selected && option.value)
      .forEach(option => {
        const customer = {
          id: option.value,
          name: option.textContent,
          identity_number: '',
          date_of_birth: '',
          email: ''
        }

        this.selectize.addOption(customer)
        this.selectize.addItem(customer.id, false) // Triggers onChange -> updateSelectedCustomersDisplay
      })
  }
}
